import React from 'react'
import { Form } from 'react-bootstrap'
import { Database, Image as ImageIcon, Layers } from 'lucide-react'
import styled from 'styled-components'

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const OptionLabel = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  display: block;
`

const SplitSelector = styled.div`
  display: flex;
  gap: 0.75rem;
`

const SplitButton = styled.button<{ $active: boolean; $type: 'training' | 'validation' }>`
  flex: 1;
  padding: 0.625rem 1rem;
  background: ${(props) =>
    props.$active
      ? props.$type === 'training'
        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
      : 'rgba(102, 126, 234, 0.1)'};
  border: 1px solid
    ${(props) =>
      props.$active
        ? props.$type === 'training'
          ? 'rgba(16, 185, 129, 0.5)'
          : 'rgba(59, 130, 246, 0.5)'
        : 'rgba(102, 126, 234, 0.2)'};
  border-radius: 8px;
  color: #fff;
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  font-size: 0.9rem;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${(props) =>
      props.$active
        ? props.$type === 'training'
          ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
          : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)'
        : 'rgba(102, 126, 234, 0.2)'};
    border-color: ${(props) =>
      props.$type === 'training' ? 'rgba(16, 185, 129, 0.6)' : 'rgba(59, 130, 246, 0.6)'};
    transform: translateY(-1px);
  }
`

const StyledCheck = styled(Form.Check)`
  .form-check-input {
    background-color: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(102, 126, 234, 0.3);
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.125rem;

    &:checked {
      background-color: #667eea;
      border-color: #667eea;
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
  }

  .form-check-label {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    margin-left: 0.5rem;
    cursor: pointer;
  }
`

const SizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const StyledSelect = styled(Form.Select)`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 8px;
  color: #fff;
  padding: 0.5rem 0.75rem;
  max-width: 120px;
  transition: all 0.3s ease;

  &:focus {
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(102, 126, 234, 0.6);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    outline: none;
  }

  option {
    background: #1e293b;
    color: #fff;
  }
`

const SummaryBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 8px;
  margin-top: 0.5rem;

  svg {
    color: #667eea;
    width: 18px;
    height: 18px;
  }

  span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-weight: 500;
  }
`

interface ExportOptionsPanelProps {
  datasetSplit: 'training' | 'validation'
  setDatasetSplit: (v: 'training' | 'validation') => void
  uploadAsUnannotated: boolean
  setUploadAsUnannotated: (v: boolean) => void
  imageSize: number
  setImageSize: (v: number) => void
  selectedCount: number
  isOBBProject?: boolean
  rotateLongSideVertical?: boolean
  setRotateLongSideVertical?: (v: boolean) => void
}

const ExportOptionsPanel: React.FC<ExportOptionsPanelProps> = ({
  datasetSplit,
  setDatasetSplit,
  uploadAsUnannotated,
  setUploadAsUnannotated,
  imageSize,
  setImageSize,
  selectedCount,
  isOBBProject = false,
  rotateLongSideVertical = false,
  setRotateLongSideVertical,
}) => {
  return (
    <OptionsContainer>
      <OptionGroup>
        <OptionLabel>Dataset Split</OptionLabel>
        <SplitSelector>
          <SplitButton
            $active={datasetSplit === 'training'}
            $type="training"
            onClick={() => setDatasetSplit('training')}
          >
            <Database size={14} />
            Training
          </SplitButton>
          <SplitButton
            $active={datasetSplit === 'validation'}
            $type="validation"
            onClick={() => setDatasetSplit('validation')}
          >
            <Database size={14} />
            Validation
          </SplitButton>
        </SplitSelector>
      </OptionGroup>

      <OptionGroup>
        <StyledCheck
          type="checkbox"
          id="uploadAsUnannotated"
          label="Upload as unannotated (no labels)"
          checked={uploadAsUnannotated}
          onChange={(e) => setUploadAsUnannotated(e.target.checked)}
        />
      </OptionGroup>

      {isOBBProject && setRotateLongSideVertical && (
        <OptionGroup>
          <StyledCheck
            type="checkbox"
            id="rotateLongSideVertical"
            label="Rotate snippets (align long side vertical)"
            checked={rotateLongSideVertical}
            onChange={(e) => setRotateLongSideVertical(e.target.checked)}
          />
        </OptionGroup>
      )}

      <OptionGroup>
        <OptionLabel>Image Size (PNG)</OptionLabel>
        <SizeSelector>
          <ImageIcon size={18} color="rgba(255, 255, 255, 0.7)" />
          <StyledSelect
            value={imageSize}
            onChange={(e) => setImageSize(parseInt(e.target.value, 10))}
          >
            <option value={256}>256px</option>
            <option value={512}>512px</option>
            <option value={640}>640px</option>
          </StyledSelect>
        </SizeSelector>
      </OptionGroup>

      <SummaryBadge>
        <Layers size={18} />
        <span>
          {selectedCount.toLocaleString()} snippet{selectedCount !== 1 ? 's' : ''} selected
        </span>
      </SummaryBadge>
    </OptionsContainer>
  )
}

export default ExportOptionsPanel
